import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import { ExpensesList, ExpensesFilter } from "components/organisms";
import { PageHeader } from "components/molecules";
import ExpensesClient from "services/api/expenses";

import { Expense } from "interfaces/expenses";
import {
	ComplexFilterType,
	DateComplexFilter,
	PropertyIdComplexFilter,
} from "interfaces/filters";

import { getPropertiesList } from "state/modules/properties";
import {
	getExpenses,
	getExpensesCount,
	getExpensesFilter,
	getExpensesList,
	getExpensesLoading,
	getExpensesTotal,
} from "state/modules/expenses";

const escapeCell = (value: string): string => {
	return `"${String(value).replace(/"/g, '""').replace(/[\r\n]+/g, " ").trim()}"`;
};

const ExpensesPage = () => {
	const dispatch = useDispatch();
	const expenses = useSelector(getExpensesList);
	const isLoading = useSelector(getExpensesLoading);
	const expensesCount = useSelector(getExpensesCount);
	const expensesTotal = useSelector(getExpensesTotal);
	const filters = useSelector(getExpensesFilter);
	const properties = useSelector(getPropertiesList);

	const hasMore = useMemo(
		() => expensesCount < expensesTotal,
		[expensesCount, expensesTotal]
	);
	const loadMore = () => {
		if (!isLoading) {
			dispatch(getExpenses(true));
		}
	};

	const handleExport = async () => {
		const dateFilter = filters.find(
			(f) => f.type === ComplexFilterType.DATE
		) as DateComplexFilter | undefined;
		const propertyFilter = filters.find(
			(f) => f.type === ComplexFilterType.PROPERTY_ID
		) as PropertyIdComplexFilter | undefined;

		const dateFrom = dateFilter?.value?.dateFrom ?? undefined;
		const dateTo = dateFilter?.value?.dateTo ?? undefined;
		const propertyIds =
			propertyFilter?.value ??
			properties.map((p) => p.id).join(",");

		const exportDate = dateFrom ? dayjs(dateFrom) : dayjs();

		try {
			const response = await ExpensesClient.getAllExpenses({
				dateFrom,
				dateTo,
				propertyIds,
			});

			const allExpenses = response.data.content as Expense[];

			const propertiesMap: Record<string, string> = {};
			properties.forEach((p) => {
				propertiesMap[p.id] = p.name;
			});

			const headers = [
				"Name",
				"Supplier",
				"Property",
				"Description",
				"Amount",
				"Date",
			];

			const rows = allExpenses.map((expense) => [
				escapeCell(expense.name || ""),
				escapeCell(expense.supplier || ""),
				escapeCell(propertiesMap[expense.propertyId] || expense.propertyId || ""),
				escapeCell(expense.description || ""),
				escapeCell(expense.amount != null ? `${expense.currency} ${expense.amount}` : ""),
				escapeCell(expense.expense_date ? dayjs(expense.expense_date).format("D MMM, YYYY") : ""),
			]);

			const csvContent = [
				headers.map(escapeCell).join(","),
				...rows.map((row) => row.join(",")),
			].join("\n");

			const blob = new Blob([csvContent], {
				type: "text/csv;charset=utf-8;",
			});
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.setAttribute("href", url);
			link.setAttribute(
				"download",
				`expenses_${exportDate.format("YYYY_MM")}.csv`
			);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Failed to export expenses:", error);
		}
	};

	return (
		<div id="expensesContainer" className="w-full h-screen overflow-auto bg-background sm:pb-24 md:h-auto md:min-h-screen">
			<PageHeader
				title="Expenses"
				description="Track and manage all property expenses in one place."
			/>
			<ExpensesFilter />
			<ExpensesList
				expenses={expenses}
				isLoading={isLoading}
				hasMore={hasMore}
				loadMore={loadMore}
				showProperty
				scrollableTarget="expensesContainer"
				onExport={handleExport}
			/>
		</div>
	);
};

export default ExpensesPage;
